import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Search, Plus, Edit3, Trash2, X, Package, ArrowDownCircle, ArrowUpCircle, AlertTriangle, Brain, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const CATEGORIES = [
  "Cimento e Argamassas","Tijolos e Blocos","Ferro e Aco","Madeira","Impermeabilizacao",
  "Tubagem e Canalizacao","Electricidade","Pintura","Isolamento","Revestimentos e Acabamentos",
  "Cobertura e Telhados","Ferragens e Fixacoes","Portas e Janelas","Equipamento Obra",
  "Mao de Obra","Seguranca Obra","Terraplanagem e Fundacoes"
];

function MarketPriceModal({ material, onClose }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const check = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      const { data } = await api.post('/materials/market-price', { name: material.name, unit: material.unit, category: material.category, current_price: material.price });
      if (data.success) setResult(data.market);
      else setError(data.error || 'Erro ao verificar preco');
    } catch { setError('Erro de conexao'); } finally { setLoading(false); }
  };

  useEffect(() => { check(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const diff = result ? ((material.price - result.avg_price) / result.avg_price * 100) : 0;
  const trendIcon = result?.price_trend === 'em_alta' ? TrendingUp : result?.price_trend === 'em_baixa' ? TrendingDown : Minus;
  const trendColor = result?.price_trend === 'em_alta' ? 'var(--error)' : result?.price_trend === 'em_baixa' ? 'var(--success)' : 'var(--text-secondary)';
  const TrendIcon = trendIcon;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="pro-card-padded w-full max-w-lg mx-4" style={{ background: 'var(--bg-card)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold flex items-center gap-2"><Brain size={16} style={{ color: 'var(--accent)' }} /> Preco de Mercado: {material.name}</h3>
          <button onClick={onClose} className="btn btn-outline btn-xs"><X size={14} /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
          Referencia de preco baseada em fontes portuguesas: INE, CYPE, AICCOPN, Primus, bases de dados de obras 2024-2026
        </p>
        <div className="p-2 rounded mb-3" style={{ background: 'var(--surface-alt)' }}>
          <span className="text-xs font-medium">Preco no sistema: </span>
          <span className="text-sm font-bold">{(material.price || 0).toFixed(2)} EUR/{material.unit}</span>
        </div>
        {loading && (
          <div className="text-center py-6">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>A consultar referencias de mercado...</p>
          </div>
        )}
        {error && <p className="text-xs text-red-600 py-4 text-center">{error}</p>}
        {result && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-3 rounded-md text-center" style={{ background: '#E8F5EC', border: '1px solid #48bb78' }}>
                <p className="text-[10px] font-medium mb-1" style={{ color: '#276749' }}>Minimo</p>
                <p className="text-sm font-bold" style={{ color: '#276749' }}>{(result.min_price || 0).toFixed(2)} EUR</p>
              </div>
              <div className="p-3 rounded-md text-center" style={{ background: '#E8F0FF', border: '1px solid #1A5FAB' }}>
                <p className="text-[10px] font-medium mb-1" style={{ color: '#1A5FAB' }}>Medio</p>
                <p className="text-sm font-bold" style={{ color: '#1A5FAB' }}>{(result.avg_price || 0).toFixed(2)} EUR</p>
              </div>
              <div className="p-3 rounded-md text-center" style={{ background: '#FFF4E0', border: '1px solid #F28C00' }}>
                <p className="text-[10px] font-medium mb-1" style={{ color: '#8B6914' }}>Maximo</p>
                <p className="text-sm font-bold" style={{ color: '#8B6914' }}>{(result.max_price || 0).toFixed(2)} EUR</p>
              </div>
            </div>
            {result.avg_price > 0 && (
              <div className={`p-2 rounded flex items-center gap-2 ${diff > 15 ? 'bg-red-50' : diff < -15 ? 'bg-green-50' : 'bg-blue-50'}`}>
                <span className="text-xs font-medium">Vs. preco no sistema:</span>
                <span className="text-xs font-bold" style={{ color: diff > 15 ? 'var(--error)' : diff < -15 ? 'var(--success)' : 'var(--accent)' }}>
                  {diff > 0 ? '+' : ''}{diff.toFixed(1)}% {diff > 15 ? '(acima do mercado)' : diff < -15 ? '(abaixo do mercado)' : '(dentro do mercado)'}
                </span>
              </div>
            )}
            {result.price_trend && (
              <div className="flex items-center gap-2">
                <TrendIcon size={14} style={{ color: trendColor }} />
                <span className="text-xs" style={{ color: trendColor }}>Tendencia: {result.price_trend === 'em_alta' ? 'Em Alta' : result.price_trend === 'em_baixa' ? 'Em Baixa' : 'Estavel'}</span>
              </div>
            )}
            {result.sources?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Fontes:</p>
                <div className="flex flex-wrap gap-1">{result.sources.map((s, i) => <span key={i} className="badge badge-info text-[10px]">{s}</span>)}</div>
              </div>
            )}
            {result.notes && <p className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>{result.notes}</p>}
            {result.regional_variation && <p className="text-[10px]" style={{ color: 'var(--text-light)' }}>Variacao regional: {result.regional_variation}</p>}
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <button onClick={check} disabled={loading} className="btn btn-outline btn-sm flex-1"><Brain size={12} /> Atualizar</button>
          <button onClick={onClose} className="btn btn-primary btn-sm">Fechar</button>
        </div>
      </div>
    </div>
  );
}

function StockModal({ material, onClose, onDone }) {
  const [type, setType] = useState('entrada');
  const [quantity, setQuantity] = useState(0);
  const [description, setDescription] = useState('');
  const [projectName, setProjectName] = useState('');

  const submit = async () => {
    if (quantity <= 0) return;
    await api.post(`/materials/${material.id}/stock`, { type, quantity, description, project_name: projectName });
    onDone();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="pro-card-padded w-full max-w-md mx-4" style={{ background: 'var(--bg-card)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold">Movimento de Stock: {material.name}</h3>
          <button onClick={onClose} className="btn btn-outline btn-xs"><X size={14} /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>Stock atual: <span className="font-bold">{material.stock || 0} {material.unit}</span></p>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button data-testid="stock-type-entrada" onClick={() => setType('entrada')} className={`btn btn-sm flex-1 ${type === 'entrada' ? 'btn-primary' : 'btn-outline'}`}>
              <ArrowDownCircle size={14} /> Entrada
            </button>
            <button data-testid="stock-type-saida" onClick={() => setType('saida')} className={`btn btn-sm flex-1 ${type === 'saida' ? 'btn-danger' : 'btn-outline'}`}>
              <ArrowUpCircle size={14} /> Saida
            </button>
          </div>
          <div><label className="field-label">Quantidade</label><input data-testid="stock-quantity" className="input" type="number" min="1" value={quantity} onChange={e => setQuantity(parseFloat(e.target.value) || 0)} /></div>
          <div><label className="field-label">Descricao</label><input className="input" placeholder="Motivo do movimento..." value={description} onChange={e => setDescription(e.target.value)} /></div>
          {type === 'saida' && <div><label className="field-label">Projeto</label><input className="input" placeholder="Nome do projeto..." value={projectName} onChange={e => setProjectName(e.target.value)} /></div>}
          <button data-testid="stock-submit" onClick={submit} className="btn btn-primary w-full">Registar Movimento</button>
        </div>
      </div>
    </div>
  );
}

export default function MaterialsPage() {
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', category: '', unit: 'un', price: 0, notes: '', stock: 0, min_stock: 0 });
  const [stockModal, setStockModal] = useState(null);
  const [showStockView, setShowStockView] = useState(false);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [marketModal, setMarketModal] = useState(null);

  const load = useCallback(() => {
    const q = search ? `/materials/search?q=${encodeURIComponent(search)}` : '/materials';
    api.get(q).then(r => setMaterials(r.data)).catch(() => {});
    api.get('/materials/stock-alerts').then(r => setStockAlerts(r.data.alerts || [])).catch(() => {});
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (editing) await api.put(`/materials/${editing.id}`, form);
    else await api.post('/materials', form);
    setEditing(null); setCreating(false); setForm({ name: '', category: '', unit: 'un', price: 0, notes: '', stock: 0, min_stock: 0 }); load();
  };

  const handleDelete = async (id) => { if (window.confirm('Eliminar?')) { await api.delete(`/materials/${id}`); load(); } };

  const startEdit = (m) => { setEditing(m); setForm({ name: m.name, category: m.category, unit: m.unit, price: m.price, notes: m.notes || '', stock: m.stock || 0, min_stock: m.min_stock || 0 }); setCreating(true); };

  const filtered = catFilter ? materials.filter(m => m.category === catFilter) : materials;
  const stockFiltered = showStockView ? filtered.filter(m => (m.stock || 0) > 0 || (m.min_stock || 0) > 0) : filtered;

  if (creating) {
    return (
      <div className="animate-in pt-10 lg:pt-0">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold">{editing ? 'Editar Material' : 'Novo Material'}</h1>
          <button onClick={() => { setCreating(false); setEditing(null); }} className="btn btn-outline btn-sm"><X size={16} /></button>
        </div>
        <div className="pro-card-padded">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="field-label">Nome</label><input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><label className="field-label">Categoria</label>
              <select className="select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                <option value="">Selecionar...</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label className="field-label">Unidade</label><input className="input" value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} /></div>
            <div><label className="field-label">Preco (EUR)</label><input className="input" type="number" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} /></div>
            <div><label className="field-label">Stock Atual</label><input className="input" type="number" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: parseFloat(e.target.value) || 0 }))} /></div>
            <div><label className="field-label">Stock Minimo (alerta)</label><input className="input" type="number" value={form.min_stock} onChange={e => setForm(p => ({ ...p, min_stock: parseFloat(e.target.value) || 0 }))} /></div>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <button onClick={() => { setCreating(false); setEditing(null); }} className="btn btn-outline">Cancelar</button>
            <button data-testid="save-material-btn" onClick={handleSave} className="btn btn-primary">Guardar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in pt-10 lg:pt-0">
      {stockModal && <StockModal material={stockModal} onClose={() => setStockModal(null)} onDone={() => { setStockModal(null); load(); }} />}
      {marketModal && <MarketPriceModal material={marketModal} onClose={() => setMarketModal(null)} />}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">Materiais e Stock</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{stockFiltered.length} de {materials.length} itens</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="toggle-stock-view" onClick={() => setShowStockView(!showStockView)} className={`btn btn-sm ${showStockView ? 'btn-primary' : 'btn-outline'}`}>
            <Package size={14} /> {showStockView ? 'Ver Todos' : 'Ver Stock'}
          </button>
          <button data-testid="new-material-btn" onClick={() => { setCreating(true); setForm({ name: '', category: '', unit: 'un', price: 0, notes: '', stock: 0, min_stock: 0 }); }} className="btn btn-primary"><Plus size={16} /> Novo</button>
        </div>
      </div>

      {/* Stock alerts */}
      {stockAlerts.length > 0 && (
        <div className="mb-4 p-3 rounded-md" style={{ background: '#FFF4E0', border: '1px solid #FFD180' }} data-testid="stock-alerts">
          <p className="text-xs font-semibold mb-2 flex items-center gap-1" style={{ color: '#8B6914' }}><AlertTriangle size={14} /> Alertas de Stock ({stockAlerts.length})</p>
          <div className="flex flex-wrap gap-2">
            {stockAlerts.slice(0, 8).map(a => (
              <span key={a.id} className={`text-[10px] font-medium px-2 py-1 rounded ${a.severity === 'critico' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {a.name}: {a.stock}/{a.min_stock} {a.unit}
              </span>
            ))}
            {stockAlerts.length > 8 && <span className="text-[10px] font-medium px-2 py-1 rounded bg-gray-100">+{stockAlerts.length - 8} mais</span>}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-light)' }} />
          <input data-testid="material-search" className="input pl-9" placeholder="Pesquisar materiais..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="select" style={{ width: 'auto' }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">Todas Categorias</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {stockFiltered.length === 0 ? (
        <div className="pro-card-padded text-center py-8"><Package size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} /><p className="font-semibold">Sem resultados</p></div>
      ) : (
        <div className="pro-card overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <table className="pro-table" data-testid="materials-table">
              <thead><tr><th>Nome</th><th>Categoria</th><th>Un.</th><th>Preco</th><th>Stock</th><th>Min.</th><th>Acoes</th></tr></thead>
              <tbody>
                {stockFiltered.map(m => {
                  const lowStock = (m.min_stock || 0) > 0 && (m.stock || 0) <= (m.min_stock || 0);
                  return (
                    <tr key={m.id} style={lowStock ? { background: '#FDECEB' } : {}}>
                      <td className="font-medium">{m.name}</td>
                      <td><span className="badge badge-neutral">{m.category}</span></td>
                      <td>{m.unit}</td>
                      <td className="font-semibold">{(m.price || 0).toFixed(2)} EUR</td>
                      <td>
                        <span className={`font-semibold ${lowStock ? 'text-red-600' : ''}`}>
                          {m.stock || 0}
                        </span>
                        {lowStock && <AlertTriangle size={12} className="inline ml-1 text-red-500" />}
                      </td>
                      <td className="text-xs" style={{ color: 'var(--text-light)' }}>{m.min_stock || '-'}</td>
                      <td>
                        <div className="flex gap-1">
                          <button data-testid={`stock-btn-${m.id}`} onClick={() => setStockModal(m)} className="btn btn-outline btn-xs" title="Movimento stock"><Package size={12} /></button>
                          <button onClick={() => setMarketModal(m)} className="btn btn-outline btn-xs" title="Verificar preco de mercado"><Brain size={12} /></button>
                          <button onClick={() => startEdit(m)} className="btn btn-outline btn-xs"><Edit3 size={12} /></button>
                          <button onClick={() => handleDelete(m.id)} className="btn btn-danger btn-xs"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
